import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAPI } from '../../../Contexts/APIContext'
import useAllCollections from '../../../Helpers/hooks/useAllCollections'
import { useAuth } from '../../../Contexts/AuthContext'
import { StickyHeader } from '../../../Components/StickyHeader'
import { Breadcrumbs } from '../../../Components/GeneralComponents/Breadcrumbs/Breadcrumbs'
import { ButtonOutline } from '../../../Components/GeneralComponents/ButtonOutline/ButtonOutline'
import TagInputSection from '../../../Components/DashboardComponents/TagInputSection'
import Prompt from '../../../Components/DashboardComponents/Prompt/Prompt'
import Loader from '../../../Components/GeneralComponents/Loader'
import errorToast from '../../../Components/Toasts/ErrorToast'
import successToast from '../../../Components/Toasts/SuccessToast'
import { usePreventFullscreen } from '../../../Helpers/hooks/usePreventFullscreen'
import { convertMillisecondsToSeconds, isPlural } from '../../../services/functions'
import { useGenerateTags } from '../CreateFeel/Step2/useGenerateTags'
import { COLLECTION_REGISTRY } from '../FeelsReview/collectionData'
import { matchCollections } from '../FeelsReview/collectionMatcher'
import styles from './index.module.css'

const BATCH_SIZE = 20
const MATCH_THRESHOLD = 0.65
const MAX_DUPLICATE_CHECK_PAGES = 30

/**
 * Parse a collection f_id from a URL or raw ID string.
 * Accepts:
 *   - full URL like https://fast.feelsmusic.app/collection/ABC123
 *   - path like /collection/ABC123
 *   - raw f_id like ABC123
 */
function parseCollectionId(input) {
  const trimmed = input.trim()
  // Try to extract from URL path  /collection/<id> or /collections/<id>
  const urlMatch = trimmed.match(/\/collections?\/([^/?#]+)/)
  if (urlMatch) return urlMatch[1]
  // Treat as raw f_id (no slashes, or just the ID string)
  const raw = trimmed.replace(/^\/+|\/+$/g, '')
  if (raw.length > 0 && !raw.includes('/')) return raw
  return null
}

const FeelsReviewByCollection = () => {
  const queryClient = useQueryClient()
  const api = useAPI()
  const { getCurrentUser } = useAuth()
  const videoRef = useRef(null)
  usePreventFullscreen(videoRef)

  // Collection input state
  const [collectionInput, setCollectionInput] = useState('')
  const [collectionFID, setCollectionFID] = useState(null)
  const [collectionName, setCollectionName] = useState('')

  // Feels state
  const [feels, setFeels] = useState([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [loading, setLoading] = useState(false)
  const [skippedIds, setSkippedIds] = useState(new Set())
  const [removedIds, setRemovedIds] = useState(new Set())
  const [removedCount, setRemovedCount] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const [isPromptDelete, setIsPromptDelete] = useState(false)
  const [suggestedTags, setSuggestedTags] = useState(null)
  const [isMuted, setIsMuted] = useState(true)

  // Refs to avoid stale closures in refetchBatch and rapid-fire guard
  const removedIdsRef = useRef(removedIds)
  removedIdsRef.current = removedIds
  const skippedIdsRef = useRef(skippedIds)
  skippedIdsRef.current = skippedIds
  const isRemovingRef = useRef(false)

  // Manual collection add state
  const [manualCollection, setManualCollection] = useState(null)
  const [collectionSearch, setCollectionSearch] = useState('')
  const [showCollectionDropdown, setShowCollectionDropdown] = useState(false)
  const collectionSearchRef = useRef(null)

  // Generate tags via API
  const {
    mutate: generateSuggestedTags,
    isPending: isLoadingGenerateSuggestedTags,
  } = useGenerateTags()

  const handleGenerateTags = (songData) => {
    generateSuggestedTags(songData, {
      onSuccess: (result) => {
        setSuggestedTags(result)
        successToast('Suggested tags generated')
      },
      onError: () => {
        errorToast('Error generating suggested tags')
      },
    })
  }

  // Fetch feels from the collection (always page 1 — removed items shift the page)
  const fetchCollectionFeels = useCallback(async (fid, initialInfo = null) => {
    try {
      setLoading(true)
      const [res, collectionInfo] = await Promise.all([
        api.getCollection(fid, 1, BATCH_SIZE),
        initialInfo ? Promise.resolve(initialInfo) : api.getCollectionById(fid),
      ])

      if (collectionInfo?.name) {
        setCollectionName(collectionInfo.name)
      }

      const items = res?.feels ?? res?.objects ?? []
      const totalFromServer = res?.feels_count ?? res?.objects_count ?? items.length
      setTotalCount(totalFromServer)

      if (!Array.isArray(items)) {
        throw new Error('Invalid response')
      }

      setFeels(items)
      setCurrentIndex(0)
      setSkippedIds(new Set())
      setRemovedIds(new Set())
      setRemovedCount(0)
      setCollectionFID(fid)
    } catch (err) {
      console.error('Failed to fetch collection feels:', err)
      errorToast('Failed to load collection')
    } finally {
      setLoading(false)
    }
  }, [api])

  // Re-fetch when local batch runs low (uses refs to avoid stale closures)
  const refetchBatch = useCallback(async () => {
    if (!collectionFID) return
    setLoading(true)
    try {
      const res = await api.getCollection(collectionFID, 1, BATCH_SIZE)
      const items = res?.feels ?? res?.objects ?? []
      const totalFromServer = res?.feels_count ?? res?.objects_count ?? items.length
      setTotalCount(totalFromServer)
      // Reset removedCount since totalCount now reflects server state
      setRemovedCount(0)

      if (items.length) {
        // Use refs for current values (avoids stale closure from the same tick)
        const newFeels = items.filter(f =>
          !skippedIdsRef.current.has(f.f_id) && !removedIdsRef.current.has(f.f_id)
        )
        setFeels(newFeels)
        setCurrentIndex(0)
      } else {
        setFeels([])
        setCurrentIndex(0)
      }
    } catch (err) {
      console.error('Failed to refetch collection:', err)
      errorToast('Failed to reload collection')
    } finally {
      setLoading(false)
    }
  }, [api, collectionFID])

  // Handle collection URL submit — validate it's a (Generated) + not public collection
  const handleLoadCollection = useCallback(async () => {
    const fid = parseCollectionId(collectionInput)
    if (!fid) {
      errorToast('Could not parse collection ID from input')
      return
    }
    setLoading(true)
    try {
      const info = await api.getCollectionById(fid)
      if (!info?.name?.includes('(Generated)')) {
        errorToast('Only (Generated) collections can be reviewed here')
        return
      }
      if (info.is_public) {
        errorToast('Cannot review a live/public collection — it must be unpublished')
        return
      }
      await fetchCollectionFeels(fid, info)
    } catch (err) {
      errorToast('Could not find that collection')
    } finally {
      setLoading(false)
    }
  }, [api, collectionInput, fetchCollectionFeels])

  const currentFeel = feels[currentIndex]

  // Queries for current feel's tags
  const { data: officialTags, isLoading: isFetchingOfficialTags } = useQuery({
    queryKey: ['officialTags', currentFeel?.f_id],
    queryFn: () => api.getFeelTags({ feelFID: currentFeel?.f_id }),
    enabled: !!currentFeel?.f_id,
    staleTime: 30000,
  })

  const { data: userTags, isLoading: isFetchingUserTags } = useQuery({
    queryKey: ['userTags', currentFeel?.f_id],
    queryFn: () => api.getFeelTags({ feelFID: currentFeel?.f_id, isUserTags: true }),
    enabled: !!currentFeel?.f_id,
    staleTime: 30000,
  })

  const { data: suggestedTagsData, isLoading: isFetchingSuggestedTags } = useQuery({
    queryKey: ['suggestedTags', currentFeel?.f_id],
    queryFn: () => api.getFeelTags({ feelFID: currentFeel?.f_id, isSuggestedTags: true }),
    enabled: !!currentFeel?.f_id,
    staleTime: 30000,
  })

  // Fetch all collections for dropdown (cached 10 min, shared with other pages)
  const { data: apiCollections, isLoading: isLoadingCollections } = useAllCollections()

  // Clear generated tags and manual collection when navigating
  useEffect(() => {
    setSuggestedTags(null)
    setManualCollection(null)
  }, [currentIndex])

  const effectiveSuggestedTags = suggestedTags || suggestedTagsData

  // Mutations
  const updateTagsMutation = useMutation({
    mutationFn: ({ feelFID, newTags }) => api.updateTagsOnFeel(feelFID, newTags),
    onSuccess: (_, { feelFID }) => {
      queryClient.invalidateQueries({ queryKey: ['officialTags', feelFID] })
      queryClient.invalidateQueries({ queryKey: ['userTags', feelFID] })
      successToast('Tags updated')
    },
    onError: () => {
      errorToast('Failed to update tags')
    }
  })

  const deleteMutation = useMutation({
    mutationFn: (feelFID) => api.deleteFeel(feelFID),
    onSuccess: (_, deletedFID) => {
      setIsPromptDelete(false)
      successToast('Feel deleted')
      setFeels(prev => {
        const newFeels = prev.filter(f => f.f_id !== deletedFID)
        setCurrentIndex(idx => {
          if (idx >= newFeels.length && newFeels.length > 0) return newFeels.length - 1
          return idx
        })
        return newFeels
      })
      // Decrement totalCount instead of incrementing removedCount
      // (delete removes the feel entirely, not just from collection)
      setTotalCount(prev => Math.max(0, prev - 1))
    },
    onError: () => {
      setIsPromptDelete(false)
      errorToast('Failed to delete Feel')
    }
  })

  // Remove feel from the source collection
  const removeFromCollectionMutation = useMutation({
    mutationFn: ({ feelFID }) => api.deleteCollectionFeel(collectionFID, feelFID),
    onError: (_, { feelFID }) => {
      // Roll back local state on failure
      setRemovedIds(prev => {
        const newSet = new Set(prev)
        newSet.delete(feelFID)
        return newSet
      })
      setRemovedCount(prev => Math.max(0, prev - 1))
      errorToast('Failed to remove from collection')
    }
  })

  const addToCollectionMutation = useMutation({
    mutationFn: async ({ collectionFID: targetFID, feelFID }) => {
      // Check if feel is already in this collection to avoid reordering
      const existingFIDs = new Set()
      let page = 1
      const pageLimit = 20
      while (page <= MAX_DUPLICATE_CHECK_PAGES) {
        const res = await api.getCollection(targetFID, page, pageLimit)
        const pageFeels = res?.feels || []
        pageFeels.forEach(f => existingFIDs.add(f.f_id))
        if (existingFIDs.has(feelFID)) {
          throw new Error('ALREADY_EXISTS')
        }
        if (pageFeels.length < pageLimit) break
        page++
      }
      return api.addFeelsToCollection(targetFID, [feelFID])
    },
    onSuccess: (_, { collectionFID: targetFID }) => {
      const collection = apiCollections?.find(c => c.f_id === targetFID)
        || COLLECTION_REGISTRY.find(c => c.f_id === targetFID)
      successToast(`Added to ${collection?.name || 'collection'}`)
    },
    onError: (error) => {
      if (error.message === 'ALREADY_EXISTS') {
        errorToast('Feel is already in this collection')
      } else {
        errorToast('Failed to add to collection')
      }
    }
  })

  const reportSyncMutation = useMutation({
    mutationFn: async (feelFID) => {
      const currentUser = getCurrentUser()
      return api.submitReport({
        report_type: 'NEEDS_SYNC',
        report_text: '',
        object: 'feels',
        object_f_id: feelFID,
        report_origin: 'FAST',
        user_f_id: currentUser?.userId || '',
        report_user_id: currentUser?.email || '',
        department: 'Feels Factory',
        report_data: { source: 'feels_review_by_collection' },
      })
    },
    onSuccess: () => {
      successToast('Needs Sync report submitted')
    },
    onError: (e) => {
      errorToast(`Error submitting report: ${e.message ?? ''}`)
    }
  })

  // NEXT: remove current feel from collection, advance
  const goToNext = useCallback(() => {
    if (!currentFeel) return
    // Use ref to prevent rapid-fire double-removal (state may not have flushed yet)
    if (isRemovingRef.current) return
    if (removedIdsRef.current.has(currentFeel.f_id)) return

    isRemovingRef.current = true

    // Remove from collection (API call)
    removeFromCollectionMutation.mutate({ feelFID: currentFeel.f_id })
    setRemovedIds(prev => {
      const newSet = new Set(prev)
      newSet.add(currentFeel.f_id)
      return newSet
    })
    setRemovedCount(prev => prev + 1)

    // Advance to next non-removed feel
    if (currentIndex < feels.length - 1) {
      setCurrentIndex(prev => prev + 1)
    } else {
      refetchBatch()
    }

    // Release guard after a tick so React state can flush
    setTimeout(() => { isRemovingRef.current = false }, 0)
  }, [currentFeel, currentIndex, feels.length, removeFromCollectionMutation, refetchBatch])

  // SKIP: advance without removing
  const goToSkip = useCallback(() => {
    if (!currentFeel) return

    setSkippedIds(prev => {
      const newSet = new Set(prev)
      newSet.add(currentFeel.f_id)
      return newSet
    })

    if (currentIndex < feels.length - 1) {
      setCurrentIndex(prev => prev + 1)
    } else {
      // At end of batch — refetch to get more (skipped ones filtered out)
      refetchBatch()
    }
  }, [currentFeel, currentIndex, feels.length, refetchBatch])

  // PREV: go back (only through skipped feels still in list)
  const goToPrev = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1)
    }
  }, [currentIndex])

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return

      switch (e.key) {
        case 'ArrowLeft':
          goToPrev()
          break
        case 'ArrowRight':
          goToNext()
          break
        case 's':
        case 'S':
          goToSkip()
          break
        default:
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [goToNext, goToPrev, goToSkip])

  const currentLyrics = useMemo(() => {
    if (!currentFeel) return ''
    return currentFeel.lyrics || currentFeel.lyrics_sync?.map(l => l.line).join(' ') || ''
  }, [currentFeel])

  const collectionMatches = useMemo(() => {
    if (!currentFeel || !currentLyrics) return []
    return matchCollections(currentLyrics, COLLECTION_REGISTRY, MATCH_THRESHOLD)
  }, [currentFeel, currentLyrics])

  const availableCollections = useMemo(() => {
    if (!apiCollections) return []
    return apiCollections
      .filter(c => !collectionMatches.some(m => m.f_id === c.f_id))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
  }, [apiCollections, collectionMatches])

  const filteredCollections = useMemo(() => {
    if (!collectionSearch.trim()) return availableCollections
    const term = collectionSearch.toLowerCase()
    return availableCollections.filter(c =>
      (c.name || '').toLowerCase().includes(term)
    )
  }, [availableCollections, collectionSearch])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (collectionSearchRef.current && !collectionSearchRef.current.contains(e.target)) {
        setShowCollectionDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Quick-pick: collections starting with "Newly Created Artist Feels"
  const newlyCreatedCollections = useMemo(() => {
    if (!apiCollections) return []
    return apiCollections
      .filter(c => c.name?.startsWith('Newly Created Artist Feels') && (c.feels_count ?? 0) > 0)
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
  }, [apiCollections])

  const breadcrumbItems = useMemo(() => [
    { text: 'Review Generated Feels' }
  ], [])

  const remaining = totalCount - removedCount

  // --- RENDER ---

  // No collection loaded yet
  if (!collectionFID) {
    return (
      <div className={styles.wrapper}>
        <StickyHeader>
          <Breadcrumbs items={breadcrumbItems} />
          <div className={styles.titleContainer}>
            <h1 className={styles.title}>Review Generated Feels</h1>
          </div>
        </StickyHeader>

        <div className={styles.warningBanner}>
          This tool <strong>removes</strong> feels from a collection as you review them. Only one person should review a collection at a time.
        </div>

        <div className={styles.loadCollectionForm}>
          <div className={styles.pickSection}>
            <label>Newly Created Artist Feels:</label>
            <select
              className={styles.collectionSelect}
              value=""
              disabled={isLoadingCollections || newlyCreatedCollections.length === 0}
              onChange={async (e) => {
                if (!e.target.value) return
                const fid = e.target.value
                setLoading(true)
                try {
                  const info = await api.getCollectionById(fid)
                  if (info.is_public) {
                    errorToast('Cannot review a live/public collection — it must be unpublished')
                    return
                  }
                  await fetchCollectionFeels(fid, info)
                } catch {
                  errorToast('Could not find that collection')
                } finally {
                  setLoading(false)
                }
              }}
            >
              <option value="">
                {isLoadingCollections
                  ? 'Loading collections...'
                  : newlyCreatedCollections.length === 0
                    ? 'No collections found'
                    : 'Select a collection...'}
              </option>
              {newlyCreatedCollections.map(c => (
                <option key={c.f_id} value={c.f_id}>
                  {c.name} ({c.feels_count ?? '?'} feels)
                </option>
              ))}
            </select>
          </div>

          <div className={styles.pickSection}>
            <label htmlFor="collectionUrl">Or paste collection URL / ID:</label>
            <div className={styles.inputRow}>
              <input
                id="collectionUrl"
                type="text"
                className={styles.collectionUrlInput}
                placeholder="https://fast.feelsmusic.app/collections/... or f_id"
                value={collectionInput}
                onChange={(e) => setCollectionInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleLoadCollection()}
              />
              <ButtonOutline color="green" size="small" text="LOAD" onClick={handleLoadCollection} />
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.loadingContainer}>
          <Loader isLoading={true} />
          <p>Loading collection...</p>
        </div>
      </div>
    )
  }

  if (feels.length === 0) {
    return (
      <div className={styles.wrapper}>
        <StickyHeader>
          <Breadcrumbs items={breadcrumbItems} />
          <div className={styles.titleContainer}>
            <h1 className={styles.title}>Review Generated Feels</h1>
          </div>
        </StickyHeader>
        <div className={styles.emptyState}>
          <p>
            {removedCount > 0
              ? `Done! Removed ${removedCount} feel${removedCount !== 1 ? 's' : ''} from "${collectionName}".`
              : `No feels found in this collection.`}
          </p>
          {skippedIds.size > 0 && (
            <p className={styles.skippedNote}>{skippedIds.size} feel{skippedIds.size !== 1 ? 's' : ''} skipped (still in collection)</p>
          )}
          <ButtonOutline
            color="pink"
            size="small"
            text="REVIEW ANOTHER COLLECTION"
            onClick={() => {
              setCollectionFID(null)
              setCollectionInput('')
              setCollectionName('')
              setFeels([])
              setRemovedIds(new Set())
              setSkippedIds(new Set())
              setRemovedCount(0)
            }}
          />
        </div>
      </div>
    )
  }

  return (
    <div className={styles.wrapper}>
      {isPromptDelete && (
        <Prompt
          title="Delete Feel"
          subtitle="Are you sure you want to delete this Feel? This action is permanent and removes it from users who saved it."
          primaryAction="CANCEL"
          onPrimaryClick={() => setIsPromptDelete(false)}
          secondaryAction="DELETE"
          onSecondaryClick={() => deleteMutation.mutate(currentFeel?.f_id)}
          isSecondaryActionDestructive={true}
          onCloseClick={() => setIsPromptDelete(false)}
        />
      )}

      <div className={styles.warningBanner}>
        NEXT <strong>removes</strong> the feel from the collection. SKIP leaves it. Only one person should review a collection at a time.
      </div>

      <StickyHeader>
        <Breadcrumbs items={breadcrumbItems} />
        <div className={styles.titleRow}>
          <h1 className={styles.title}>
            {collectionName || 'Review Generated Feels'}
          </h1>
          <div className={styles.headerControls}>
            <div className={styles.progress}>
              {remaining} remaining{removedCount > 0 && ` (${removedCount} removed)`}
              {skippedIds.size > 0 && ` (${skippedIds.size} skipped)`}
            </div>
            <ButtonOutline
              color="pink"
              size="small"
              text="CHANGE COLLECTION"
              onClick={() => {
                setCollectionFID(null)
                setCollectionInput('')
                setCollectionName('')
                setFeels([])
                setRemovedIds(new Set())
                setSkippedIds(new Set())
                setRemovedCount(0)
              }}
            />
          </div>
        </div>
      </StickyHeader>

      {currentFeel && removedIds.has(currentFeel.f_id) && (
        <div className={styles.removedBadge}>REMOVED FROM COLLECTION</div>
      )}

      <div className={styles.songNavRow}>
        <div className={styles.feelInfo}>
          <a
            href={`/song/${currentFeel?.song_f_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.songTitleLink}
          >
            <h2 className={styles.songTitle}>{currentFeel?.song_name}</h2>
          </a>
          <a
            href={`/artist/${currentFeel?.artist?.artist_name}`}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.artistNameLink}
          >
            <h3 className={styles.artistName}>{currentFeel?.song_artist_name}</h3>
          </a>
          <a
            href={`/feel/${currentFeel?.f_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.feelId}
          >
            {currentFeel?.f_id}
          </a>
        </div>

        <div className={styles.navButtonsTop}>
          <ButtonOutline
            color="red"
            size="small"
            text="DELETE"
            onClick={() => setIsPromptDelete(true)}
          />
          <ButtonOutline
            color="pink"
            size="small"
            text={reportSyncMutation.isPending ? 'REPORTING...' : 'REPORT SYNC'}
            disabled={reportSyncMutation.isPending}
            onClick={() => reportSyncMutation.mutate(currentFeel?.f_id)}
          />
          <div className={styles.navSpacer} />
          <ButtonOutline
            color="pink"
            size="small"
            text="← PREV"
            disabled={currentIndex === 0}
            onClick={goToPrev}
          />
          <ButtonOutline
            color="blue"
            size="small"
            text="SKIP →"
            onClick={goToSkip}
          />
          <ButtonOutline
            color={currentFeel && removedIds.has(currentFeel.f_id) ? 'gray' : 'green'}
            size="small"
            text={
              currentFeel && removedIds.has(currentFeel.f_id)
                ? 'ALREADY REMOVED'
                : removeFromCollectionMutation.isPending ? 'REMOVING...' : 'NEXT (REMOVE) →'
            }
            disabled={removeFromCollectionMutation.isPending || (currentFeel && removedIds.has(currentFeel.f_id))}
            onClick={goToNext}
          />
        </div>
      </div>

      <div className={styles.contentWrapper}>
        {/* LEFT: Video */}
        <div className={styles.feelsColumn}>
          <div className={styles.videoContainer}>
            <video
              ref={videoRef}
              key={currentFeel?.f_id}
              className={styles.videoPlayer}
              controls
              autoPlay
              muted={isMuted}
              loop
              src={currentFeel?.preview?.video?.video?.url}
              controlsList="nofullscreen"
              onVolumeChange={(e) => setIsMuted(e.target.muted)}
            />
          </div>

          <div className={styles.lyrics}>
            <ul className={styles.lyricsText}>
              {currentFeel?.lyrics_sync?.map(({ line }, index) => (
                <li key={`${line}-${index}`}>{line}</li>
              ))}
            </ul>
            {currentFeel?.video_duration && (
              <p className={styles.duration}>
                {convertMillisecondsToSeconds(currentFeel.video_duration)} second
                {isPlural(convertMillisecondsToSeconds(currentFeel.video_duration))}
              </p>
            )}
          </div>
        </div>

        {/* CENTER: Tags */}
        <div className={styles.tagsColumn}>
          <TagInputSection
            key={currentFeel?.f_id}
            disabled={updateTagsMutation.isPending || isFetchingOfficialTags || isFetchingUserTags}
            officialTags={officialTags?.tags}
            userTags={userTags?.tags_user}
            suggestedTags={effectiveSuggestedTags?.tags || []}
            handleTagsUpdated={(newTags) => updateTagsMutation.mutate({ feelFID: currentFeel?.f_id, newTags })}
            isFetchingOfficialTags={isFetchingOfficialTags}
            isFetchingUserTags={isFetchingUserTags}
            isFetchingSuggestedTags={isFetchingSuggestedTags || isLoadingGenerateSuggestedTags}
            handleSuggestionTagClick={{
              mutate: (newTags) => updateTagsMutation.mutate({ feelFID: currentFeel?.f_id, newTags }),
              isPending: updateTagsMutation.isPending,
            }}
            refetchSuggestedTags={handleGenerateTags}
            song={currentFeel}
            isTagsUpdatingSuccess={updateTagsMutation.isSuccess}
          />
        </div>

        {/* RIGHT: Collections */}
        <div className={styles.collectionsColumn}>
          <h4 className={styles.sectionTitle}>Add to Another Collection</h4>

          {/* Manual collection selector */}
          <div className={styles.manualSelector} ref={collectionSearchRef}>
            <input
              type="text"
              className={styles.collectionSearchInput}
              placeholder="Search collections..."
              value={collectionSearch}
              onChange={(e) => setCollectionSearch(e.target.value)}
              onFocus={() => setShowCollectionDropdown(true)}
            />
            {showCollectionDropdown && (
              <div className={styles.collectionDropdownList}>
                {filteredCollections.length === 0 ? (
                  <div className={styles.noCollectionResults}>No collections found</div>
                ) : (
                  filteredCollections.map(c => (
                    <div
                      key={c.f_id}
                      className={styles.collectionDropdownItem}
                      onClick={() => {
                        setManualCollection(c)
                        setCollectionSearch('')
                        setShowCollectionDropdown(false)
                      }}
                    >
                      {c.name}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Manual selection */}
          {manualCollection && (
            <div className={`${styles.collectionItem} ${styles.manualSelection}`}>
              <div className={styles.collectionInfo}>
                <span className={styles.collectionName}>{manualCollection.name}</span>
                <button
                  className={styles.clearManual}
                  onClick={() => setManualCollection(null)}
                >
                  ✕
                </button>
              </div>
              <span className={styles.manualLabel}>Manual selection</span>
              <ButtonOutline
                color="green"
                size="small"
                text={addToCollectionMutation.isPending && addToCollectionMutation.variables?.collectionFID === manualCollection.f_id ? '...' : 'ADD'}
                disabled={addToCollectionMutation.isPending}
                onClick={() => {
                  addToCollectionMutation.mutate({ collectionFID: manualCollection.f_id, feelFID: currentFeel?.f_id })
                  setManualCollection(null)
                }}
              />
            </div>
          )}

          <h4 className={styles.sectionTitle}>Suggested Collections</h4>

          {collectionMatches.length > 0 ? (
            <div className={styles.collectionsList}>
              {collectionMatches.map((match) => (
                <div key={match.f_id} className={styles.collectionItem}>
                  <div className={styles.collectionInfo}>
                    <span className={styles.collectionName}>{match.name}</span>
                    <span className={styles.matchScore}>{Math.round(match.score * 100)}%</span>
                  </div>
                  <span className={styles.matchedKeyword}>&quot;{match.matchedKeyword}&quot;</span>
                  <ButtonOutline
                    color="green"
                    size="small"
                    text={addToCollectionMutation.isPending && addToCollectionMutation.variables?.collectionFID === match.f_id ? '...' : 'ADD'}
                    disabled={addToCollectionMutation.isPending}
                    onClick={() => addToCollectionMutation.mutate({ collectionFID: match.f_id, feelFID: currentFeel?.f_id })}
                  />
                </div>
              ))}
            </div>
          ) : (
            !manualCollection && <p className={styles.noMatches}>No collection matches found</p>
          )}
        </div>
      </div>

      <div className={styles.keyboardHints}>
        Keyboard: ← Prev | → Next (Remove) | S Skip
      </div>
    </div>
  )
}

export default FeelsReviewByCollection
